import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { doc, getDoc, setDoc, deleteDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { compressImage } from "../../firebase/upload";
import { FiUpload, FiCheck, FiPlus, FiTrash2 } from "react-icons/fi";

const defaultSouthDakota = [
  { name: "My Safety Plan", url: "https://mysafetyplan.org" },
  { name: "South Dakota Suicide Prevention", url: "https://www.sdsuicideprevention.org/" },
  { name: "Department of Social Services", url: "https://dss.sd.gov/behavioralhealth/default.aspx" },
  { name: "seizetheawkward", url: "https://seizetheawkward.org/" },
  { name: "liveinspired365", url: "https://liveinspired365.org/" },
  { name: "journeyofhopesd", url: "https://www.journeyofhopesd.org/" },
  { name: "sdbehavioralhealth", url: "https://sdbehavioralhealth.gov/recovery/peer-support" },
  { name: "South Dakota National Alliance on Mental Health", url: "https://namisouthdakota.org/" },
  { name: "Peer Support", url: "https://sdbehavioralhealth.gov/recovery/peer-support" },
];

const defaultNorthDakota = [
  { name: "hhs.nd.gov", url: "https://www.hhs.nd.gov/news/health-and-human-services-recognizes-mental-health-awareness-month-shares-resources-support" },
  { name: "Veterans", url: "https://www.veterans.nd.gov/behavioral-mental-health" },
  { name: "mhand", url: "https://www.mhand.org/" },
  { name: "ndthrives", url: "https://ndthrives.com/access/" },
  { name: "hhs.nd.gov", url: "https://www.hhs.nd.gov/behavioral-health/directory" },
  { name: "nami north dakota", url: "https://naminorthdakota.org/" },
];

const defaultNationwide = [
  { name: "The National Widowers' Organization", url: "https://nationalwidowers.org/" },
  { name: "Hopenet", url: "https://hopenet.org/" },
  { name: "lyrahealth", url: "https://www.lyrahealth.com/resources/mental-health-stigma/" },
  { name: "Safety plan", url: "https://www.mysafetyplan.org/" },
  { name: "National Alliance on Mental Illness", url: "https://nami.org" },
  { name: "National Institute for Mental Health", url: "https://www.nimh.nih.gov/health/find-help" },
  { name: "Veterans Crisis Line", url: "https://www.veteranscrisisline.net/" },
  { name: "Substance Abuse and Mental Health Services Administration (SAMHSA)", url: "https://www.samhsa.gov/find-support" },
  { name: "How to Overcome Stigma in the workplace", url: "https://www.lyrahealth.com/resources/mental-health-stigma/" },
  { name: "Dbs Alliance", url: "https://www.dbsalliance.org/" },
];

const defaultContent = {
  heroTitle: "RESOURCES",
  breadcrumb: "Home / Resources",
  southDakotaResources: defaultSouthDakota,
  northDakotaResources: defaultNorthDakota,
  nationwideResources: defaultNationwide,
  quote1Text: "There is hope, even when your brain tells you there isn't.",
  quote1Author: "John Green",
  quote1Gradient: "from-[#e6ac00] via-[#f0a53a] to-[#f38e67]",
  quote2Text: "What mental health needs is more sunlight, more candor, and more unashamed conversation.",
  quote2Author: "Glenn Close",
  quote2Gradient: "from-[#50a7bf] via-[#59b7a9] to-[#7cc87d]",
  bottomImage: "",
  namiTitle: "nami",
  namiSubtitle: "National Alliance on Mental Illness",
  namiDescription: "The NAMI HelpLine provides the one-on-one help and information necessary to tackle tough challenges.",
  namiCall: "1-800-950-NAMI",
  namiChat: "Text \"HelpLine\" to 62640",
  namiCrisis: "Call or Text 988",
  crisis988Title: "988",
  crisis988Subtitle: "SUICIDE & CRISIS LIFELINE",
  crisis988Description: "We can help prevent suicide. The 988 Lifeline provides 24/7, free and confidential support for people in distress, prevention and crisis resources for you or your loved ones, and best practices for professionals in the United States.",
  subscribeHeading: "Get The Latest Updates",
  subscribePlaceholder: "Enter your email",
  subscribeButtonText: "Subscribe",
};

const ResourceSection = ({ title, section, items, onAdd, onUpdate, onRemove }) => (
  <div className="bg-white rounded-xl border border-gray-100 p-6">
    <div className="flex items-center justify-between mb-5">
      <h2 className="text-[14px] font-extrabold">{title}</h2>
      <button type="button" onClick={() => onAdd(section)}
        className="flex items-center gap-1 text-[12px] text-[#e84fa5] font-semibold hover:text-pink-700 transition">
        <FiPlus size={14} /> Add Resource
      </button>
    </div>
    <div className="space-y-3">
      {items.map((item, i) => (
        <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[11px] font-bold text-gray-400">Resource {i + 1}</span>
            {items.length > 1 && (
              <button type="button" onClick={() => onRemove(section, i)} className="text-red-400 hover:text-red-600 transition">
                <FiTrash2 size={14} />
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input value={item.name} onChange={(e) => onUpdate(section, i, "name", e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="Resource name" />
            <input value={item.url} onChange={(e) => onUpdate(section, i, "url", e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[#e84fa5]" placeholder="https://..." />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const MentalHealthContent = () => {
  const [content, setContent] = useState(defaultContent);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const snap = await getDoc(doc(db, "settings", "mentalHealthContent"));
        if (snap.exists()) {
          const data = snap.data();
          setContent((prev) => ({
            ...prev,
            ...data,
            southDakotaResources: data.southDakotaResources || defaultSouthDakota,
            northDakotaResources: data.northDakotaResources || defaultNorthDakota,
            nationwideResources: data.nationwideResources || defaultNationwide,
          }));
        }
      } catch (e) {
        if (e.code === "resource-exhausted" || (e.message && e.message.includes("exceeds"))) {
          alert("Document too large! Click 'Reset All Data' to clear old images and start fresh.");
        }
      }
      setLoading(false);
    };
    fetch();
  }, []);

  const handleUpload = async (e, field, maxW = 800, quality = 0.5) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading((u) => ({ ...u, [field]: true }));
    try {
      const base64 = await compressImage(file, maxW, quality);
      setContent((s) => ({ ...s, [field]: base64 }));
      await setDoc(doc(db, "settings", "mentalHealthContent"), { [field]: base64 }, { merge: true });
    } catch (e) { alert("Upload failed: " + e.message); }
    setUploading((u) => ({ ...u, [field]: false }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await setDoc(doc(db, "settings", "mentalHealthContent"), content, { merge: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { alert("Save failed: " + e.message + "\n\nTry clicking 'Reset All Data' first to clear old large images."); }
    setSaving(false);
  };

  const handleReset = async () => {
    if (!window.confirm("Reset ALL mental health resources page data to defaults? This will remove all uploaded images and reset text fields.")) return;
    setSaving(true);
    try {
      await deleteDoc(doc(db, "settings", "mentalHealthContent"));
      await setDoc(doc(db, "settings", "mentalHealthContent"), defaultContent);
      setContent(defaultContent);
      alert("Document reset successfully! You can now upload images with optimized compression.");
    } catch (e) { alert("Reset failed: " + e.message); }
    setSaving(false);
  };

  const updateResource = (section, i, field, val) => {
    const list = [...content[section]];
    list[i] = { ...list[i], [field]: val };
    setContent((s) => ({ ...s, [section]: list }));
  };

  const addResource = (section) => {
    setContent((s) => ({ ...s, [section]: [...s[section], { name: "", url: "" }] }));
  };

  const removeResource = (section, i) => {
    setContent((s) => ({ ...s, [section]: s[section].filter((_, idx) => idx !== i) }));
  };

  if (loading) {
    return <AdminLayout><div className="flex justify-center py-20"><div className="w-8 h-8 border-4 border-[#e84fa5] border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        <div className="mb-6">
          <h1 className="text-2xl font-extrabold text-black">Mental Health Resources Page</h1>
          <p className="text-sm text-gray-400">Manage all content displayed on the Mental Health Resources page</p>
        </div>

        <form onSubmit={handleSave} className="space-y-8">
          {/* Hero / Breadcrumb */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">Hero / Breadcrumb</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Hero Title</label>
                <input value={content.heroTitle} onChange={(e) => setContent((s) => ({ ...s, heroTitle: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Breadcrumb Text</label>
                <input value={content.breadcrumb} onChange={(e) => setContent((s) => ({ ...s, breadcrumb: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
            </div>
          </div>

          {/* Resources - Upar */}
          <ResourceSection
            title="South Dakota Resources"
            section="southDakotaResources"
            items={content.southDakotaResources}
            onAdd={addResource}
            onUpdate={updateResource}
            onRemove={removeResource}
          />

          <ResourceSection
            title="North Dakota Resources"
            section="northDakotaResources"
            items={content.northDakotaResources}
            onAdd={addResource}
            onUpdate={updateResource}
            onRemove={removeResource}
          />

          <ResourceSection
            title="Nationwide / USA-Wide Resources"
            section="nationwideResources"
            items={content.nationwideResources}
            onAdd={addResource}
            onUpdate={updateResource}
            onRemove={removeResource}
          />

          {/* Quotes */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">Quote Card 1 (Orange Gradient)</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Quote Text</label>
                <textarea value={content.quote1Text} onChange={(e) => setContent((s) => ({ ...s, quote1Text: e.target.value }))} rows={3}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5] resize-y" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Author</label>
                <input value={content.quote1Author} onChange={(e) => setContent((s) => ({ ...s, quote1Author: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">Quote Card 2 (Blue/Green Gradient)</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Quote Text</label>
                <textarea value={content.quote2Text} onChange={(e) => setContent((s) => ({ ...s, quote2Text: e.target.value }))} rows={3}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5] resize-y" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Author</label>
                <input value={content.quote2Author} onChange={(e) => setContent((s) => ({ ...s, quote2Author: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
            </div>
          </div>

          {/* Bottom Image */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">Bottom Image</h2>
            <div className="bg-gray-50 rounded-xl p-5">
              <label className="block text-[12px] font-bold text-gray-600 mb-3">Image</label>
              {content.bottomImage && (
                <img src={content.bottomImage} alt="Bottom" className="h-24 w-auto object-contain mb-3 bg-white rounded border p-1" />
              )}
              {!content.bottomImage && (
                <div className="h-24 bg-gray-200 rounded mb-3 flex items-center justify-center text-[11px] text-gray-400 border-2 border-dashed">Bottom Image</div>
              )}
              <label className="flex items-center gap-2 cursor-pointer w-fit px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-[#e84fa5] text-[13px] text-gray-500 hover:text-[#e84fa5] transition">
                <FiUpload size={14} />
                {uploading.bottomImage ? "Uploading..." : "Upload Image"}
                <input type="file" accept="image/*" onChange={(e) => handleUpload(e, "bottomImage")} className="hidden" disabled={uploading.bottomImage} />
              </label>
            </div>
          </div>

          {/* NAMI Sidebar */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">NAMI Sidebar Card</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Title</label>
                <input value={content.namiTitle} onChange={(e) => setContent((s) => ({ ...s, namiTitle: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Subtitle</label>
                <input value={content.namiSubtitle} onChange={(e) => setContent((s) => ({ ...s, namiSubtitle: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Description</label>
                <textarea value={content.namiDescription} onChange={(e) => setContent((s) => ({ ...s, namiDescription: e.target.value }))} rows={2}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5] resize-y" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Call Number</label>
                <input value={content.namiCall} onChange={(e) => setContent((s) => ({ ...s, namiCall: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Chat/Text Info</label>
                <input value={content.namiChat} onChange={(e) => setContent((s) => ({ ...s, namiChat: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Crisis Info</label>
                <input value={content.namiCrisis} onChange={(e) => setContent((s) => ({ ...s, namiCrisis: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
            </div>
          </div>

          {/* 988 Sidebar */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">988 Crisis Lifeline Sidebar Card</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Title (big number)</label>
                <input value={content.crisis988Title} onChange={(e) => setContent((s) => ({ ...s, crisis988Title: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Subtitle</label>
                <input value={content.crisis988Subtitle} onChange={(e) => setContent((s) => ({ ...s, crisis988Subtitle: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Description</label>
                <textarea value={content.crisis988Description} onChange={(e) => setContent((s) => ({ ...s, crisis988Description: e.target.value }))} rows={3}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5] resize-y" />
              </div>
            </div>
          </div>

          {/* Subscribe */}
          <div className="bg-white rounded-xl border border-gray-100 p-6">
            <h2 className="text-[14px] font-extrabold mb-5">Subscribe Section</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Heading</label>
                <input value={content.subscribeHeading} onChange={(e) => setContent((s) => ({ ...s, subscribeHeading: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Input Placeholder</label>
                <input value={content.subscribePlaceholder} onChange={(e) => setContent((s) => ({ ...s, subscribePlaceholder: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
              <div>
                <label className="block text-[12px] font-bold text-gray-600 mb-1.5">Button Text</label>
                <input value={content.subscribeButtonText} onChange={(e) => setContent((s) => ({ ...s, subscribeButtonText: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] outline-none focus:border-[#e84fa5]" />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <button type="submit" disabled={saving}
              className="px-8 py-3 bg-[#e84fa5] text-white font-bold text-[13px] uppercase rounded-lg hover:bg-pink-600 transition disabled:opacity-60">
              {saving ? "Saving..." : "Save Mental Health Resources Page"}
            </button>
            {saved && (
              <span className="flex items-center gap-2 text-green-600 text-[13px] font-semibold">
                <FiCheck /> Mental Health Resources page saved!
              </span>
            )}
            <button type="button" onClick={handleReset} disabled={saving}
              className="px-4 py-2 text-[11px] text-red-500 border border-red-300 rounded-lg hover:bg-red-50 transition font-semibold">
              Reset All Data
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
};

export default MentalHealthContent;
